import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Select, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, Chip,
  IconButton, CircularProgress, FormControl, InputLabel, Alert, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WifiTetheringRoundedIcon from '@mui/icons-material/WifiTetheringRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import axios from 'axios';
import t from '../i18n';
import { Page, Card } from '../ui';

// Статусы бэкенда: unknown | active | error
const STATUS = {
  active: { color: 'success', label: 'Активен' },
  error:  { color: 'error',   label: 'Ошибка' },
};

// Код страны (RU) → флаг-эмодзи (🇷🇺)
const flag = (code) => {
  if (!code || code.length !== 2) return '';
  return String.fromCodePoint(...[...code.toUpperCase()].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
};

// Прокси → строка host:port[:user:pass] для редактирования
const proxyToString = (px) =>
  [px.host, px.port, px.user, px.password].filter(Boolean).join(':');

export default function ProxiesPage() {
  const [input, setInput] = useState('');
  const [protocol, setProtocol] = useState('HTTP');
  const [proxies, setProxies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [validating, setValidating] = useState(false);
  const [checkingId, setCheckingId] = useState(null);
  const [toast, setToast] = useState(null); // { t: 'success'|'warning'|'error', x: текст }

  const load = () => {
    setLoading(true);
    axios.get('/proxies').then(r => setProxies(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const add = () => {
    if (!input.trim()) return;
    setAdding(true); setToast(null);
    // Бэкенд ждёт сырой текст (по одному прокси на строку) + протокол.
    axios.post('/proxies/bulk', { proxies: input.trim(), protocol })
      .then((r) => {
        const { added = 0, skipped = 0 } = r.data || {};
        if (added === 0 && skipped > 0) setToast({ t: 'warning', x: 'Эти прокси уже есть в списке - ничего не добавлено' });
        else if (skipped > 0) setToast({ t: 'success', x: `Добавлено: ${added}. Пропущено дублей: ${skipped}` });
        else setToast({ t: 'success', x: `Добавлено: ${added}` });
        setInput(''); load();
      })
      .catch((e) => setToast({ t: 'error', x: (e.response && e.response.data && e.response.data.detail) || 'Не удалось добавить прокси' }))
      .finally(() => setAdding(false));
  };

  const remove = (id) => {
    axios.delete(`/proxies/${id}`).then(load)
      .catch(() => setToast({ t: 'error', x: 'Не удалось удалить' }));
  };

  const validate = () => {
    setValidating(true); setToast(null);
    axios.post('/proxies/check-all')
      .then((r) => {
        const { checked = 0, active = 0, warning } = r.data || {};
        setToast(warning
          ? { t: 'error', x: warning }
          : { t: active > 0 ? 'success' : 'warning', x: `Проверено: ${checked}, живых: ${active}` });
        load();
      })
      .catch((e) => setToast({ t: 'error', x: (e.response && e.response.data && e.response.data.detail) || 'Проверка не удалась (ошибка сервера)' }))
      .finally(() => setValidating(false));
  };

  // Редактирование: { id, value, protocol } или null
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const saveEdit = () => {
    if (!editing || !editing.value.trim()) return;
    setSavingEdit(true); setToast(null);
    axios.put(`/proxies/${editing.id}`, { proxy_string: editing.value.trim(), protocol: editing.protocol })
      .then(() => {
        setToast({ t: 'success', x: 'Прокси обновлён. Статус сброшен - проверь его заново' });
        setEditing(null); load();
      })
      .catch((e) => setToast({ t: 'error', x: (e.response && e.response.data && e.response.data.detail) || 'Не удалось сохранить' }))
      .finally(() => setSavingEdit(false));
  };

  const checkOne = (id) => {
    setCheckingId(id); setToast(null);
    axios.get(`/proxies/${id}/check`)
      .then((r) => {
        const p = r.data || {};
        setToast(p.save_error
          ? { t: 'error', x: `Не сохранилось: ${p.save_error}` }
          : p.status === 'active'
            ? { t: 'success', x: `Прокси живой. Внешний IP: ${p.ip || '?'}` }
            : { t: 'error', x: `Прокси не работает: ${p.error || 'ошибка'}` });
        load();
      })
      .catch(() => setToast({ t: 'error', x: 'Проверка не удалась (ошибка сервера)' }))
      .finally(() => setCheckingId(null));
  };

  const headSx = { fontSize: 11, fontWeight: 700, color: 'text.secondary', letterSpacing: '0.06em', textTransform: 'uppercase' };

  return (
    <Page title={t.proxiesTitle} subtitle={t.proxiesSubtitle}>
      {/* Форма добавления */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography component="span" sx={{ fontSize: 15, fontWeight: 700, color: 'text.primary' }}>{t.addProxies}</Typography>
          <Typography component="span" sx={{ fontSize: 12, color: 'text.secondary', ml: 1 }}>· {t.onePerLine}</Typography>
        </Box>
        <TextField multiline rows={4} fullWidth value={input} onChange={e => setInput(e.target.value)}
          placeholder={'192.168.1.1:8080\n10.0.0.1:1080:user:pass'}
          sx={{ mb: 2, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 13 } }} />
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t.protocol}</InputLabel>
            <Select value={protocol} label={t.protocol} onChange={e => setProtocol(e.target.value)}>
              {['HTTP', 'HTTPS', 'SOCKS5'].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
          </FormControl>
          <Button onClick={add} disabled={adding || !input.trim()} variant="contained" sx={{ px: 3 }}>
            {adding ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t.addProxiesBtn}
          </Button>
          <Button onClick={validate} disabled={validating || proxies.length === 0} variant="outlined" startIcon={<CheckCircleRoundedIcon />} sx={{ px: 3 }}>
            {validating ? <CircularProgress size={18} /> : t.validateAll}
          </Button>
        </Box>
        {toast && <Alert severity={toast.t} onClose={() => setToast(null)} sx={{ mt: 2, borderRadius: '6px' }}>{toast.x}</Alert>}
      </Card>

      {/* Таблица */}
      <Card sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'text.primary' }}>{t.proxyList}</Typography>
            <Chip label={proxies.length} size="small" color="success" variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
          </Box>
          <IconButton onClick={load} size="small" sx={{ color: 'text.secondary' }}>
            {loading ? <CircularProgress size={16} /> : <RefreshRoundedIcon fontSize="small" />}
          </IconButton>
        </Box>
        <Table sx={{ '& td, & th': { borderColor: 'divider' } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={headSx}>{t.host}</TableCell>
              <TableCell sx={headSx}>{t.port}</TableCell>
              <TableCell sx={headSx}>{t.protocol}</TableCell>
              <TableCell sx={headSx}>{t.auth}</TableCell>
              <TableCell sx={headSx}>Гео</TableCell>
              <TableCell sx={headSx}>{t.status}</TableCell>
              <TableCell sx={{ ...headSx, width: 130 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {proxies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 7, textAlign: 'center', border: 'none' }}>
                  <ShieldRoundedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>{t.noProxies}</Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>{t.noProxiesDesc}</Typography>
                </TableCell>
              </TableRow>
            ) : proxies.map(px => {
              const sc = STATUS[px.status] || { color: 'default', label: 'Не проверен' };
              return (
                <TableRow key={px.id} hover>
                  <TableCell sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', fontFamily: 'monospace' }}>{px.host}</TableCell>
                  <TableCell sx={{ fontSize: 13, color: 'text.secondary', fontFamily: 'monospace' }}>{px.port}</TableCell>
                  <TableCell>
                    <Chip label={px.protocol} size="small" color="info" variant="outlined" sx={{ height: 22, fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{px.user ? '●●●●●●' : '—'}</TableCell>
                  <TableCell sx={{ fontSize: 13, color: 'text.primary', whiteSpace: 'nowrap' }}>
                    {px.geo ? <>{flag(px.geo)} {px.geo}</> : '—'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={px.status === 'error' ? (px.error || '') : (px.ip ? `IP: ${px.ip}` : '')}>
                      <Chip label={sc.label} size="small" color={sc.color} variant="outlined" sx={{ height: 22, fontSize: 11, fontWeight: 600 }} />
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Просмотр и редактирование">
                      <IconButton size="small" onClick={() => setEditing({ id: px.id, value: proxyToString(px), protocol: (px.protocol || 'socks5').toUpperCase() })} sx={{ color: 'text.secondary', '&:hover': { color: 'info.main' } }}>
                        <EditRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Проверить этот прокси">
                      <span>
                        <IconButton size="small" disabled={checkingId === px.id} onClick={() => checkOne(px.id)} sx={{ color: 'text.secondary', '&:hover': { color: 'success.main' } }}>
                          {checkingId === px.id ? <CircularProgress size={14} /> : <WifiTetheringRoundedIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <IconButton size="small" onClick={() => remove(px.id)} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Просмотр / редактирование прокси */}
      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Прокси</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
            Формат: host:port или host:port:user:pass. После сохранения статус сбросится - проверь прокси заново.
          </Typography>
          <TextField
            label="Прокси" fullWidth autoFocus value={editing ? editing.value : ''}
            onChange={(e) => setEditing((p) => ({ ...p, value: e.target.value }))}
            sx={{ mb: 2, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 13 } }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{t.protocol}</InputLabel>
            <Select value={editing ? editing.protocol : 'SOCKS5'} label={t.protocol}
              onChange={(e) => setEditing((p) => ({ ...p, protocol: e.target.value }))}>
              {['HTTP', 'HTTPS', 'SOCKS5'].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditing(null)}>Отмена</Button>
          <Button variant="contained" onClick={saveEdit} disabled={savingEdit || !editing || !editing.value.trim()}>
            {savingEdit ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
